import React, { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { WizardProgress } from '@/components/agency/wizard/WizardProgress';
import { useDomainVerification } from '@/hooks/useDomainVerification';
import { DomainStep1Enter } from '@/components/agency/domain/DomainStep1Enter';
import { DomainStep2ConfigureDNS } from '@/components/agency/domain/DomainStep2ConfigureDNS';
import { DomainStep3Verify } from '@/components/agency/domain/DomainStep3Verify';
import { DomainStep4Complete } from '@/components/agency/domain/DomainStep4Complete';
import { Globe } from 'lucide-react';

const WIZARD_STEPS = [
  { label: 'Enter Domain', description: 'Add your custom domain' },
  { label: 'Configure DNS', description: 'Update your DNS settings' },
  { label: 'Verify', description: 'Confirm domain ownership' },
  { label: 'Complete', description: 'Domain is ready' },
];

export default function DomainVerificationPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const {
    verification,
    loading,
    checking,
    polling,
    initiateDomain,
    checkVerification,
    startPolling,
    stopPolling,
    reset,
  } = useDomainVerification();

  const handleDomainSubmit = async (domain: string) => {
    const result = await initiateDomain(domain);
    if (result) {
      setCurrentStep(1);
    }
  };

  const handleProceedToDNS = () => {
    setCurrentStep(1);
  };

  const handleProceedToVerify = () => {
    if (verification) {
      setCurrentStep(2);
      startPolling(verification.id);
    }
  };

  const handleCheckNow = async () => {
    if (verification) {
      const result = await checkVerification(verification.id);
      if (result?.status === 'verified') {
        setCurrentStep(3);
      }
    }
  };

  const handleStartOver = () => {
    reset();
    setCurrentStep(0);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      if (currentStep === 2) {
        stopPolling();
      }
      setCurrentStep(currentStep - 1);
    }
  };

  // Auto-advance to complete when verified
  React.useEffect(() => {
    if (verification?.status === 'verified' && currentStep === 2) {
      stopPolling();
      setCurrentStep(3);
    }
  }, [verification?.status, currentStep, stopPolling]);

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Custom Domain</h1>
          </div>
          <p className="text-muted-foreground">
            Connect your own domain to white-label your Calie.ai dashboard
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader className="pb-4">
            <WizardProgress currentStep={currentStep} steps={WIZARD_STEPS} />
          </CardHeader>
        </Card>

        <Card>
          <CardContent className="pt-6">
            {currentStep === 0 && (
              <DomainStep1Enter
                onSubmit={handleDomainSubmit}
                loading={loading}
              />
            )}

            {currentStep === 1 && verification && (
              <DomainStep2ConfigureDNS
                verification={verification}
                onNext={handleProceedToVerify}
                onBack={handleBack}
              />
            )}

            {currentStep === 2 && verification && (
              <DomainStep3Verify
                verification={verification}
                checking={checking}
                polling={polling}
                onCheckNow={handleCheckNow}
                onBack={handleBack}
              />
            )}

            {currentStep === 3 && verification && (
              <DomainStep4Complete
                verification={verification}
                onStartOver={handleStartOver}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
